import { DOCUMENT } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  MatAccordion,
  MatCheckboxChange,
  MatDialog,
  MatSnackBar,
} from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { ErgoEvalCompleteEvaluationDialogComponent } from "app/assessment/evaluation/components/ergo-eval-complete-evaluation-dialog/ergo-eval-complete-evaluation-dialog.component";
import { ErgonomicsEvaluationReportDialogComponent } from "app/assessment/evaluation/components/ergonomics-evaluation-report-dialog/ergonomics-evaluation-report-dialog.component";
import { EvaluationInterface } from "app/assessment/evaluation/models";
import { EvaluationService } from "app/assessment/evaluation/services/evaluation.service";
import { mapEvaluationMeasurement } from "app/assessment/evaluation/utilities/evaluation-measurement-mapper.utility";
import { AssessmentPageInterface, EntityType } from "app/assessment/models";
import { ReviewInterface } from "app/assessment/models/review.model";
import { CreateEntityForm } from "app/assessment/utilities/create-entity-form";
import { UpdateEntity } from "app/assessment/utilities/update-entity";
import { UserInterface } from "app/core/user/models";
import { UserService } from "app/core/user/services";
import utilityCommon from "app/core/utility/utility-common";
import { EmployeeEquipmentWorkflowInterface } from "app/equipment/models";
import { RecommendationInterface } from "app/recommendation/models";
import { RecommendationCategoryInterface } from "app/recommendation/models/recommendation-category.model";
import { GlobalConstants } from "app/shared/constants/globalconstants";
import { getDeepCopyOfObject } from "app/shared/utilities/utility-methods.utility";
import { TaskInterface } from "app/task/models";
import { BehaviorSubject, interval, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "cz-evaluation-stepper",
  templateUrl: "./evaluation-stepper.component.html",
  styleUrls: ["./evaluation-stepper.component.scss"],
})
export class EvaluationStepperComponent implements OnInit, OnDestroy {
  @Input() public evaluation: EvaluationInterface;
  @Input() public currentUser: UserInterface;
  @Input() public currentTask: TaskInterface;
  @Input() public recommendationTemplates$: BehaviorSubject<
    Readonly<RecommendationCategoryInterface[]>
  >;
  @Input() public adminUsers: UserInterface[];

  @Input() public recommendations$: BehaviorSubject<
    Readonly<RecommendationInterface[]>
  >;
  @Input() public reviews$: BehaviorSubject<Readonly<ReviewInterface[]>>;

  @Input() public recommendedEquipment$: BehaviorSubject<
    Readonly<EmployeeEquipmentWorkflowInterface[]>
  >;
  @Input() public installedEquipment$: BehaviorSubject<
    Readonly<EmployeeEquipmentWorkflowInterface[]>
  >;

  @Output() public recommendationAdded: EventEmitter<RecommendationInterface> =
    new EventEmitter<RecommendationInterface>();
  @Output()
  public recommendationUpdated: EventEmitter<RecommendationInterface> =
    new EventEmitter<RecommendationInterface>();
  @Output()
  public recommendationRemoved: EventEmitter<RecommendationInterface> =
    new EventEmitter<RecommendationInterface>();
  @Output() public reviewAdded: EventEmitter<ReviewInterface> =
    new EventEmitter<ReviewInterface>();
  @Output() public reviewUpdated: EventEmitter<ReviewInterface> =
    new EventEmitter<ReviewInterface>();
  @Output() public reviewRemoved: EventEmitter<ReviewInterface> =
    new EventEmitter<ReviewInterface>();
  public adminUsers$: BehaviorSubject<Readonly<UserInterface[]>>;
  public saved$: BehaviorSubject<Readonly<boolean>>;

  public czEvaluationForm: FormGroup = new FormGroup({});
  public panelOpenState: boolean = false;
  public customFieldActive: boolean = false;
  public editingComment: boolean = false;
  @ViewChild("myaccordion") public myPanels: MatAccordion;
  public evaluationImagePath: string =
    "/assets/images/components/cz-assessment/";
  public updatedEvaluation: EvaluationInterface;
  public updatedTask: TaskInterface;
  public readOnly: boolean = false;
  public mapEvalualtionMeasurements: Function = mapEvaluationMeasurement;
  public browser: string = "unknown";

  get formArray(): AbstractControl | null {
    return this.czEvaluationForm.get("formArray");
  }

  public czEvaluationRequiredFormFields: FormGroup;
  public evaluationDateControl: FormControl = new FormControl(
    "",
    Validators.required,
  );
  public assessedByControl: FormControl = new FormControl(
    "",
    Validators.required,
  );

  // these groups used for the view
  public ergoEvalPageGroup: AssessmentPageInterface[];
  public surveyAnswersPageGroup: AssessmentPageInterface[];
  public evaluationReviewPageGroup: AssessmentPageInterface[];
  public ergoEvalDetailsGroup: AssessmentPageInterface[];
  public ergoEvalWorkAreaGroup: AssessmentPageInterface[];
  public equipmentEvalPageGroup: AssessmentPageInterface[];

  // pdf array (for what pages to include in the pdf)
  public pdfArray: string[] = [];
  public isAllSelected: boolean = false;

  // for photo upload
  public placeholderImage: string = `${this.evaluationImagePath}section-img-upload/icon-upload.svg`;
  public componentName: string = EntityType.EVALUATION;
  public userId: number;
  public EvaladminUsers: UserInterface[];
  public cultureCookie: number;
  public backupLanguageId: number;
  public noTranslate: string;

  // grouping the pages together via their IDs
  private ergoEvalGroup: string[] = ["1-1", "2-1", "3-1", "4-1", "5-1"];
  private surveyResultsGroup: string[] = [
    "6-1",
    "7-1",
    "8-1",
    "9-1",
    "10-1",
    "11-1",
    "12-1",
    "13-1",
  ];
  private equipmentEvalGroup: string[] = ["14-1", "15-1"];
  private ergoEvalReviewGroup: string[] = ["16-1"];
  private ergoDetailsGroup: string[] = ["17-1"];
  private ergoWorkAreaGroup: string[] = ["18-1"];

  private readonly autosaveInterval: number = 60000;
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _formBuilder: FormBuilder,
    private _createEvaluationForm: CreateEntityForm,
    public dialog: MatDialog,
    private _updateEvaluation: UpdateEntity,
    private _evaluationService: EvaluationService,
    private _translateService: TranslateService,
    private readonly _userService: UserService,
    private _snackBar: MatSnackBar,
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private readonly _GlobalConstants: GlobalConstants,
  ) {
    this.noTranslate = this._translateService.instant("$common_no_translation");
    this.browser = utilityCommon.getBrowserName();
    this.saved$ = new BehaviorSubject<Readonly<boolean>>(undefined);
  }

  public ngOnInit(): void {
    /*  */
    this.cultureCookie = this._GlobalConstants.cultureCookieId;
    this.backupLanguageId = this._GlobalConstants.backupLanguageId;

    /*  */
    this.EvaladminUsers = this.adminUsers;

    // Initialize auto-save.
    interval(this.autosaveInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.autoSave();
      });

    if (!this.evaluation || !this.currentTask) {
      return;
    }

    this.readOnly = this.evaluation.isComplete;
    const formArray: FormArray = this._createEvaluationForm.createFormArray(
      this.evaluation.pages,
      true,
      this.readOnly,
    );
    this.czEvaluationForm = this._formBuilder.group({ formArray });
    this.buildPageGroups(this.evaluation.pages);
    this.userId = this.evaluation.subject.id;
    this.updatedEvaluation = this.evaluation;
    this.updatedTask = this.currentTask;

    if (
      this.EvaladminUsers === undefined ||
      this.evaluation.printPages === null ||
      this.evaluation.printPages === undefined
    ) {
      setTimeout(() => {
        // Data undefined
        this.loadEvaloationData();
      }, 3000);
    } else {
      this.loadEvaloationData();
    }
  }

  public loadEvaloationData(): void {
    // this.adminUsers = null;
    if (this.adminUsers === null || this.adminUsers === undefined) {
      this.loadAdminUsers();
    } else {
      this.EvaladminUsers = this.adminUsers;
      this.buildRequiredFormFieldsGroup();
      if (
        this.evaluation !== null &&
        this.evaluation !== undefined &&
        this.evaluation.printPages !== null
      ) {
        this.evaluation.printPages.forEach((pageId: string) => {
          this.pdfArray.push(pageId);
        });
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public openAll(): void {
    this.myPanels.openAll();
    this.panelOpenState = true;
  }
  public closeAll(): void {
    this.myPanels.closeAll();
    this.panelOpenState = false;
  }

  public toggleSelectAll(): void {
    if (this.isAllSelected) {
      this.unselectAll();
    } else {
      this.selectAll();
    }
    this.isAllSelected = !this.isAllSelected;
  }

  private unselectAll(): void {
    this.pdfArray = [];
    this.updatedEvaluation = {
      ...this.updatedEvaluation,
      printPages: this.pdfArray,
    };
  }

  public selectAll(): void {
    this.pdfArray = this.ergoEvalGroup.concat(
      this.surveyResultsGroup,
      this.equipmentEvalGroup,
      ["equipment"],
    );
    this.updatedEvaluation = {
      ...this.updatedEvaluation,
      printPages: this.pdfArray,
    };
  }

  public openPdfDialog(): void {
    const pageParam: string = `?page=${this.pdfArray}`;
    // checking for tablet device
    if (utilityCommon.isTablet()) {
      this.showSnackBar(this._translateService.instant("$pdf_ios_preparing"));
      window.location.assign(
        `/api/pdf/assessment/${this.evaluation.id}${pageParam}`,
      );
    } else {
      // Download pdf in Safari
      if (this.browser === "Safari") {
        this.showSnackBar(
          this._translateService.instant("$pdf_android_download"),
        );
        utilityCommon.pdfDownloadinSafari(this.evaluation.id, pageParam);
        // window.location.assign(`/api/pdf/assessment_download/${this.evaluation.id}${pageParam}`);
      } else {
        this.dialog.open(ErgonomicsEvaluationReportDialogComponent, {
          height: "100%",
          width: "1000px",
          data: {
            evaluationId: this.evaluation.id,
            pages: this.pdfArray,
          },
          panelClass: "recommendations-report-pdf",
        });
      }
    }
  }

  public addCustomField(): void {
    // Max of 5 custom fields allowed per Dave
    this.customFieldActive = true;
  }

  public editComment(): void {
    if (this.editingComment) {
      this.editingComment = false;
    } else if (!this.editingComment) {
      this.editingComment = true;
    }
  }

  public saveEvaluation(): void {
    if (
      !this.czEvaluationRequiredFormFields.value.date ||
      !this.czEvaluationRequiredFormFields.value.assessedBy
    ) {
      // scroll to top of page to show errors.
      // Using scrollTop on the actual scrollable container (mat-sidenav-content)
      // instead of scrollIntoView() which is unreliable in Safari.
      const scrollContainer: Element = this._document.querySelector(
        "mat-sidenav-content",
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      } else {
        this._document.querySelector(".page-view-content").scrollIntoView();
      }
      return;
    }

    // update the evaluation
    this.doEvalUpdate();
  }

  public completeEvaluationDialog(): void {
    this.doEvalUpdate(true);
  }

  public checkboxInteraction(event: MatCheckboxChange, pageId: string): void {
    if (event.checked) {
      // add pageId to array
      this.pdfArray.push(pageId);
    } else {
      // remove pageId from array
      this.pdfArray = this.pdfArray.filter((id: string) => id !== pageId);
    }
    this.updatedEvaluation = {
      ...this.updatedEvaluation,
      printPages: this.pdfArray,
    };
  }

  public stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  public forwardReviewAdded(review: ReviewInterface): void {
    this.reviewAdded.emit(review);
  }

  public forwardReviewUpdated(review: ReviewInterface): void {
    this.reviewUpdated.emit(review);
  }

  public forwardReviewRemoved(review: ReviewInterface): void {
    this.reviewRemoved.emit(review);
  }

  public forwardRecommendationAdded(
    recommendation: RecommendationInterface,
  ): void {
    this.recommendationAdded.emit(recommendation);
  }

  public forwardRecommendationUpdated(
    recommendation: RecommendationInterface,
  ): void {
    this.recommendationUpdated.emit(recommendation);
  }

  public forwardRecommendationRemoved(
    recommendation: RecommendationInterface,
  ): void {
    this.recommendationRemoved.emit(recommendation);
  }

  public photoRemoved(): void {
    this.saveEvaluation();
  }

  public hasSpecialText(groupId: string): boolean {
    return this.getSpecialText(groupId).length > 0;
  }

  public getSpecialText(groupId: string): string {
    const label: string = this.mapEvalualtionMeasurements(groupId);
    if (label) {
      return this._translateService.instant(label);
    }
    return "";
  }

  private buildPageGroups(evaluationPages: AssessmentPageInterface[]): void {
    this.ergoEvalPageGroup = this.loopThroughPagesToCreateGroup(
      this.ergoEvalGroup,
      evaluationPages,
    );
    this.surveyAnswersPageGroup = this.loopThroughPagesToCreateGroup(
      this.surveyResultsGroup,
      evaluationPages,
    );
    this.equipmentEvalPageGroup = this.loopThroughPagesToCreateGroup(
      this.equipmentEvalGroup,
      evaluationPages,
    );
    this.evaluationReviewPageGroup = this.loopThroughPagesToCreateGroup(
      this.ergoEvalReviewGroup,
      evaluationPages,
    );
    this.ergoEvalDetailsGroup = this.loopThroughPagesToCreateGroup(
      this.ergoDetailsGroup,
      evaluationPages,
    );
    this.ergoEvalWorkAreaGroup = this.loopThroughPagesToCreateGroup(
      this.ergoWorkAreaGroup,
      evaluationPages,
    );
  }

  private loopThroughPagesToCreateGroup(
    pageGroupKeys: string[],
    evaluationPages: AssessmentPageInterface[],
  ): AssessmentPageInterface[] {
    let group: AssessmentPageInterface[] = [];

    for (const key in pageGroupKeys) {
      if (!pageGroupKeys.hasOwnProperty(key)) {
        continue;
      }

      group = group.concat(
        evaluationPages.filter(
          (page: AssessmentPageInterface) => page.id === pageGroupKeys[key],
        ),
      );
    }

    return group;
  }

  private buildRequiredFormFieldsGroup(): void {
    if (
      this.evaluation !== null &&
      this.evaluation !== undefined &&
      this.evaluation.date
    ) {
      this.evaluationDateControl = new FormControl(
        { value: this.evaluation.date, disabled: this.readOnly },
        Validators.required,
      );
    }
    if (
      this.evaluation !== null &&
      this.evaluation !== undefined &&
      this.evaluation.admin
    ) {
      this.assessedByControl = new FormControl("", Validators.required);
      if (this.EvaladminUsers !== null && this.EvaladminUsers !== undefined) {
        this.EvaladminUsers = this.EvaladminUsers.filter(
          (user: UserInterface) => user.id !== this.evaluation.admin.id,
        );
        this.EvaladminUsers.push(this.evaluation.admin);
      }
      this.assessedByControl.patchValue(this.evaluation.admin);
      if (this.readOnly) {
        this.assessedByControl.disable();
      }
    }
    this.czEvaluationRequiredFormFields = new FormGroup({
      date: this.evaluationDateControl,
      assessedBy: this.assessedByControl,
    });
  }

  private loadAdminUsers(): void {
    this._userService
      .findAllAdminUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe((adminUsers: UserInterface[]) => {
        if (!adminUsers) {
          return;
        }
        // this.adminUsers$.next(adminUsers);
        this.adminUsers = adminUsers;
        this.EvaladminUsers = this.adminUsers;
        this.buildRequiredFormFieldsGroup();
        if (
          this.evaluation !== null &&
          this.evaluation !== undefined &&
          this.evaluation.printPages !== null
        ) {
          this.evaluation.printPages.forEach((pageId: string) => {
            this.pdfArray.push(pageId);
          });
        }
      });
  }

  private doEvalUpdate(closeTask: boolean = false): void {
    this.saved$.next(true);
    this.updatedEvaluation = this._updateEvaluation.updateEvaluation(
      this.updatedEvaluation,
      this.czEvaluationForm,
    );
    this.updatedEvaluation.date =
      this.czEvaluationRequiredFormFields.value.date;
    this.updatedEvaluation.admin =
      this.czEvaluationRequiredFormFields.value.assessedBy;
    this.updatedEvaluation.printPages = getDeepCopyOfObject(
      this.pdfArray,
    ) as string[];
    this._evaluationService.updateCurrentEvaluation(this.updatedEvaluation);
    this.showSnackBar(this._translateService.instant("$message_eval_saved"));
    if (closeTask) {
      this.dialog.open(ErgoEvalCompleteEvaluationDialogComponent, {
        height: "230px",
        width: "600px",
        data: { evaluation: this.evaluation, task: this.currentTask },
      });
    }
  }

  private autoSave(): void {
    if (!this.czEvaluationForm.dirty) {
      return;
    }

    this.doEvalUpdate();
    this.czEvaluationForm.markAsPristine();
  }

  private showSnackBar(message: string): void {
    this._snackBar.open(message, undefined, {
      duration: 5000,
    });
  }
}
